import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {aiBatteryIconTooltipText} from "@khan/guide-utils";

import BatteryIcon from "../icons/battery-icon.tsx";

interface Props {
    /**
     * A flag indicating whether AI Guide is experiencing high traffic.
     * Defaults to false.
     */
    isHighTraffic?: boolean;

    /**
     * A float value representing the remaining usage quota.
     */
    remainingQuota?: number | null;
    /**
     * Optional style overrides. Main usage is for positioning within the
     * parent component.
     */
    style?: StyleType;
    /**
     * The color of the icon. If unset, defaults to offWhite.
     */
    color?: string;
}

export const PowerIndicator = (props: Props): React.ReactElement => {
    const {isHighTraffic = false, remainingQuota, color, style} = props;
    return (
        <View testId="ai-power" style={[styles.wrapper, style]}>
            {remainingQuota != null && (
                <Tooltip
                    content={aiBatteryIconTooltipText(
                        remainingQuota,
                        isHighTraffic,
                    )}
                    contentStyle={styles.tooltipContent}
                    placement="bottom"
                >
                    <View style={styles.iconWrapper}>
                        {isHighTraffic && (
                            <View
                                testId="ai-traffic-indicator"
                                style={styles.trafficIndicator}
                            />
                        )}
                        <BatteryIcon
                            fraction={remainingQuota}
                            style={styles.icon}
                            color={color}
                            remainingQuota={remainingQuota}
                            isHighTraffic={isHighTraffic}
                        />
                    </View>
                </Tooltip>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    // Placeholder space for the AI power battery, so the header doesn't reflow
    // when remainingQuota comes in and the battery renders.
    wrapper: {
        height: 32,
        // hide the power indicator
        display: "none",
    },
    icon: {
        // HACK: Since the SVG is not centered in its viewport, we apply this
        // margin to center it. When we move to Phosphor icons, we may not need
        // this margin anymore.
        marginBlockStart: spacing.xxxxSmall_2,
    },
    iconWrapper: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    tooltipContent: {
        whiteSpace: "pre-wrap",
    },
    trafficIndicator: {
        backgroundColor: color.gold,
        height: 8,
        width: 8,
        borderRadius: "50%",
        marginInlineEnd: spacing.xxxSmall_4,
    },
});
